"use client";

import type { EquationBalanceSpec } from "@/lib/whiteboard/spec";
import { ArrowRight, Check, Equal, Scale } from "lucide-react";
import { useEffect, useState } from "react";
import { WhiteboardWriteInText } from "./WhiteboardWriteInText";
import type { WhiteboardCardEventHandler } from "./types";

export function EquationBalanceCard({
  spec,
  onEvent,
  variant = "dark",
  highlightTargets = [],
}: {
  spec: EquationBalanceSpec;
  onEvent?: WhiteboardCardEventHandler;
  variant?: "dark" | "photo";
  highlightTargets?: string[];
}) {
  const [attempt, setAttempt] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [stage, setStage] = useState<"operation" | "answer">("operation");
  const [resolved, setResolved] = useState(spec.status === "resolved");
  const [operationPending, setOperationPending] = useState(false);
  const isPhotoVariant = variant === "photo";
  const isHighlighted = (targetId: string) => highlightTargets.includes(targetId);

  useEffect(() => {
    setAttempt(0);
    setSelectedId(null);
    setOperationPending(false);
  }, [spec.id]);

  useEffect(() => {
    setSelectedId(null);
    setOperationPending(false);
    setStage(spec.status === "verifying" || spec.status === "resolved" ? "answer" : "operation");
    setResolved(spec.status === "resolved");
  }, [spec.equation, spec.status]);

  const choose = (choice: EquationBalanceSpec["choices"][number]) => {
    if (resolved || operationPending) return;
    const nextAttempt = attempt + 1;
    setAttempt(nextAttempt);
    setSelectedId(choice.id);
    if (choice.correct) setOperationPending(true);
    onEvent?.({
      event: choice.correct ? "working" : "answered",
      response: choice.operationId,
      correct: choice.correct,
      action: "selected_operation",
      target: choice.label,
      expected: spec.choices.find((item) => item.correct)?.operationId,
      progress: choice.correct
        ? "correct_first_operation"
        : nextAttempt === 1
          ? "hint"
          : "guided_step",
      resolved: false,
      attempt: nextAttempt,
      targetId: `equation_balance.choice.${choice.id}`,
    });
  };

  const chooseAnswer = (choice: EquationBalanceSpec["answerChoices"][number]) => {
    if (resolved) return;
    const nextAttempt = attempt + 1;
    setAttempt(nextAttempt);
    setSelectedId(choice.id);
    if (choice.correct) setResolved(true);
    onEvent?.({
      event: choice.correct ? "completed" : "answered",
      response: choice.answerId,
      correct: choice.correct,
      action: "selected_solution",
      target: choice.label,
      expected: spec.answerChoices.find((item) => item.correct)?.answerId,
      progress: choice.correct ? "resolved" : nextAttempt === 1 ? "hint" : "guided_step",
      resolved: choice.correct,
      attempt: nextAttempt,
      targetId: `equation_balance.answer.${choice.id}`,
    });
  };

  return (
    <section
      className={
        isPhotoVariant
          ? "equation-balance-card--photo text-[#162033]"
          : "rounded-lg border border-white/10 bg-[#15161B] p-4 text-white sm:p-6"
      }
      aria-labelledby={`${spec.id}-title`}
      data-task-id={spec.taskId}
      data-attempt-id={spec.attemptId}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          {isPhotoVariant ? (
            <>
              <h2
                id={`${spec.id}-title`}
                className="text-xl font-bold leading-snug text-[#162033] sm:text-2xl"
              >
                <WhiteboardWriteInText text={spec.title} streamKey={`${spec.id}:title`} inline />
              </h2>
              <p className="mt-1 text-sm leading-6 text-[#4C5968] sm:text-base">
                <WhiteboardWriteInText
                  text={spec.prompt}
                  streamKey={`${spec.id}:prompt`}
                  initialDelayMs={280}
                  inline
                />
              </p>
            </>
          ) : (
            <>
              <h2 id={`${spec.id}-title`} className="text-lg font-bold text-white sm:text-xl">
                {spec.title}
              </h2>
              <p className="mt-1 text-sm leading-6 text-white/60">{spec.prompt}</p>
            </>
          )}
        </div>
        <Scale
          className={`h-6 w-6 shrink-0 ${isPhotoVariant ? "text-[#1E5A86]" : "text-[#FD954F]"}`}
          aria-hidden="true"
        />
      </div>

      {spec.lastStep ? (
        <div
          key={spec.lastStep.stepId}
          role="status"
          aria-live="polite"
          className={`mt-5 rounded-lg border p-3 sm:p-4 ${
            isPhotoVariant
              ? "border-[#1E5A86]/25 bg-white/55"
              : "border-[#FD954F]/35 bg-[#FD954F]/10"
          }`}
          data-testid="equation-step-change"
        >
          <p
            className={`text-xs font-bold uppercase tracking-[0.14em] ${
              isPhotoVariant ? "text-[#1E5A86]" : "text-[#FDB47F]"
            }`}
          >
            What changed
          </p>
          <p
            className={`mt-1 text-sm font-semibold ${
              isPhotoVariant ? "text-[#162033]" : "text-white"
            }`}
          >
            {spec.lastStep.operationLabel}
          </p>
          <div className="mt-3 grid gap-2 font-mono text-sm sm:text-base">
            <span
              className={`min-w-0 rounded-md px-2 py-2 text-center ${
                isPhotoVariant ? "bg-white/45 text-[#4C5968]" : "bg-black/20 text-white/55"
              }`}
            >
              {spec.lastStep.fromEquation}
            </span>
            {spec.lastStep.workingEquation ? (
              <>
                <ArrowRight
                  className={`mx-auto h-4 w-4 rotate-90 shrink-0 ${
                    isPhotoVariant ? "text-[#1E5A86]" : "text-[#FD954F]"
                  }`}
                  aria-hidden="true"
                />
                <span
                  className={`min-w-0 rounded-md border px-2 py-2 text-center font-semibold ${
                    isPhotoVariant
                      ? "border-[#1E5A86]/25 bg-white/65 text-[#162033]"
                      : "border-[#FD954F]/25 bg-[#FD954F]/10 text-[#FFD9B8]"
                  }`}
                  data-testid="equation-working-line"
                >
                  {spec.lastStep.workingEquation}
                </span>
              </>
            ) : null}
            <ArrowRight
              className={`mx-auto h-4 w-4 rotate-90 shrink-0 ${
                isPhotoVariant ? "text-[#1E5A86]" : "text-[#FD954F]"
              }`}
              aria-hidden="true"
            />
            <span
              className={`min-w-0 rounded-md px-2 py-2 text-center font-bold ${
                isPhotoVariant ? "bg-white/70 text-[#162033]" : "bg-black/30 text-white"
              }`}
            >
              {spec.lastStep.toEquation}
            </span>
          </div>
        </div>
      ) : null}

      <div
        className="my-6 grid grid-cols-[1fr_auto_1fr] items-center gap-3 font-mono text-2xl font-bold sm:my-8 sm:text-4xl"
        aria-label={`Equation ${spec.equation}`}
      >
        <div
          className={`min-w-0 px-3 py-5 text-center transition-colors ${
            isPhotoVariant
              ? "border-b-2 border-[#1E5A86]/25 text-[#162033]"
              : "rounded-lg border border-white/10 bg-[#0F0F14]"
          } ${isHighlighted("left_side") ? "ring-2 ring-[#FD954F] ring-offset-2 ring-offset-transparent" : ""}`}
          data-whiteboard-target-id="left_side"
          data-whiteboard-highlighted={isHighlighted("left_side")}
        >
          {isPhotoVariant ? (
            <WhiteboardWriteInText
              as="span"
              text={spec.left}
              streamKey={`${spec.id}:${spec.equation}:left`}
              initialDelayMs={560}
              inline
            />
          ) : (
            spec.left
          )}
        </div>
        <Equal
          className={`h-7 w-7 transition-colors ${
            isHighlighted("equality_marker")
              ? "rounded-md bg-[#FD954F]/20 text-[#FD954F] ring-2 ring-[#FD954F]"
              : isPhotoVariant
                ? "text-[#1E5A86]"
                : "text-[#FD954F]"
          }`}
          aria-hidden="true"
          data-whiteboard-target-id="equality_marker"
          data-whiteboard-highlighted={isHighlighted("equality_marker")}
        />
        <div
          className={`min-w-0 px-3 py-5 text-center transition-colors ${
            isPhotoVariant
              ? "border-b-2 border-[#1E5A86]/25 text-[#162033]"
              : "rounded-lg border border-white/10 bg-[#0F0F14]"
          } ${isHighlighted("right_side") ? "ring-2 ring-[#FD954F] ring-offset-2 ring-offset-transparent" : ""}`}
          data-whiteboard-target-id="right_side"
          data-whiteboard-highlighted={isHighlighted("right_side")}
        >
          {isPhotoVariant ? (
            <WhiteboardWriteInText
              as="span"
              text={spec.right}
              streamKey={`${spec.id}:${spec.equation}:right`}
              initialDelayMs={760}
              inline
            />
          ) : (
            spec.right
          )}
        </div>
      </div>

      <div
        className={`mx-auto mb-6 h-1.5 max-w-md rounded-full ${
          isPhotoVariant ? "bg-[#1E3A5F]/15" : "bg-white/10"
        }`}
        aria-hidden="true"
      >
        <div
          className={`mx-auto h-1.5 w-1/2 rounded-full ${
            isPhotoVariant ? "bg-[#1E5A86]/55" : "bg-[#FD954F]/70"
          }`}
        />
      </div>

      {(spec.hint || (attempt === 1 && !resolved)) && (
        <p
          className={`mb-3 rounded-lg border px-3 py-2 text-sm ${
            isPhotoVariant
              ? "border-[#1E5A86]/25 bg-white/65 text-[#233F58]"
              : "border-[#FD954F]/30 bg-[#FD954F]/10 text-[#FFD9B8]"
          }`}
        >
          {spec.hint || "Keep both sides equal. Make the same change on each side."}
        </p>
      )}
      {(spec.guidedStep || (attempt >= 2 && !resolved)) && (
        <p
          className={`mb-3 rounded-lg border px-3 py-2 text-sm ${
            isPhotoVariant
              ? "border-[#1E3A5F]/20 bg-white/55 text-[#344A5F]"
              : "border-white/12 bg-white/[0.04] text-white/75"
          }`}
        >
          {spec.guidedStep ||
            "Start with the operation furthest from x, then use it on both sides."}
        </p>
      )}

      <div
        className="grid gap-2"
        role="group"
        aria-label={stage === "operation" ? "Choose the next equation step" : "Choose the solution"}
      >
        {(stage === "operation" ? spec.choices : spec.answerChoices).map((choice) => {
          const selected = selectedId === choice.id;
          return (
            <button
              key={choice.id}
              type="button"
              onClick={() =>
                stage === "operation"
                  ? choose(choice as EquationBalanceSpec["choices"][number])
                  : chooseAnswer(choice as EquationBalanceSpec["answerChoices"][number])
              }
              disabled={resolved || operationPending}
              className={`min-h-12 rounded-lg border px-4 py-3 text-left text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FD954F] disabled:cursor-default ${
                selected && choice.correct
                  ? "border-[#FD954F] bg-[#FD954F] text-[#1C1C1E]"
                  : selected
                    ? "border-red-400/50 bg-red-400/10 text-red-100"
                    : "border-white/12 bg-[#1B1C22] text-white hover:border-[#FD954F]/60"
              }`}
              data-whiteboard-target-id={`equation_balance.choice.${choice.id}`}
            >
              <span className="flex items-center justify-between gap-3">
                <span>{choice.label}</span>
                {selected && choice.correct ? (
                  <Check className="h-4 w-4" aria-hidden="true" />
                ) : null}
              </span>
            </button>
          );
        })}
      </div>

      <p
        className={`mt-4 text-xs ${isPhotoVariant ? "text-[#4C5968]" : "text-white/45"}`}
        aria-live="polite"
      >
        {resolved
          ? "Step verified. The tutor will respond next."
          : operationPending
            ? "Updating the equation…"
            : attempt === 0
              ? "You can also explain your answer aloud."
              : "That has not resolved the task yet."}
      </p>
    </section>
  );
}
